import React from 'react';
import fallbackData from './ProfileSkins.json';
import { getProfileSkins } from '../services/api';

const API_URL = (process.env.REACT_APP_API_URL || '').replace(/\/+$/, '');

// Skin art comes from two places: files shipped in the website build under
// /img/..., and files admins upload, which live on the API. The stored path
// says which, so nothing has to be flagged in the database.
export function resolveSkinAssetUrl(value) {
    if (!value) return value;
    if (/^https?:\/\//i.test(value)) return value;
    if (value.startsWith('/skinassets/')) return `${API_URL}${value}`;
    if (value.startsWith('/skin-assets/')) return `${API_URL}${value.replace('/skin-assets/', '/skinassets/')}`;
    return value;
}

const resolveSkin = (skin) => ({
    ...skin,
    ...(skin.backgroundImage ? { backgroundImage: resolveSkinAssetUrl(skin.backgroundImage) } : {}),
    ...(skin.assets ? { assets: skin.assets.map(a => ({ ...a, image: resolveSkinAssetUrl(a.image) })) } : {}),
    ...(skin.presetBackgrounds
        ? { presetBackgrounds: skin.presetBackgrounds.map(p => ({ ...p, image: resolveSkinAssetUrl(p.image) })) }
        : {})
});

const FALLBACK_SKINS = fallbackData.skins || [];

let skins = FALLBACK_SKINS;
let loadPromise = null;

const listeners = new Set();

const notify = () => {
    listeners.forEach(fn => {
        try { fn(); } catch {}
    });
};

export function getSkins() {
    return skins;
}

export function getStaffSkinIds() {
    return skins.filter(s => s.staffOnly).map(s => s.id);
}

export function getPresetBackgrounds() {
    return skins.flatMap(s =>
        (s.presetBackgrounds || []).map((p, i) => ({
            ...p,
            skinId: s.id,
            id: i === 0 ? s.id : `${s.id}_${i}`,
            label: s.name
        }))
    );
}

export function isStaffOnlySkin(skinId) {
    if (!skinId) return false;
    const skin = skins.find(s => s.id === skinId);
    return skin ? skin.staffOnly === true : false;
}

export function isSkinSaveable(skinId, isStaff, ownedSkinIds = []) {
    if (!skinId || skinId === 'default') return true;
    if (isStaffOnlySkin(skinId)) return isStaff;
    return ownedSkinIds.includes(skinId);
}

export function loadProfileSkins(force = false) {
    if (loadPromise && !force) return loadPromise;

    loadPromise = getProfileSkins()
        .then(res => {
            if (res?.success && Array.isArray(res.skins) && res.skins.length) {
                skins = res.skins.map(resolveSkin);
                notify();
            }
            return skins;
        })
        .catch(() => skins);

    return loadPromise;
}

export function useProfileSkins() {
    const [, bump] = React.useReducer(x => x + 1, 0);

    React.useEffect(() => {
        listeners.add(bump);
        loadProfileSkins();
        return () => { listeners.delete(bump); };
    }, []);

    return skins;
}
